import { useLoaderData } from '@remix-run/react';
import { useCountryContext } from '@archipro-website/localisation';
import { NewsletterTypeEnum } from 'generated/graphql';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { ComingSoonPill } from '../components/ComingSoonPill';
import { ScrollReveal } from '../components/ScrollReveal';
import { SubscribeField } from '../components/SubscribeField';
import portfolioImg from '../images/magazine/portfolio.jpg';
import chairImg from '../images/magazine/chair.jpg';
import herringboneImg from '../images/magazine/herringbone.jpg';
import stoveImg from '../images/magazine/stove.jpg';
import commercialImg from '../images/magazine/commercial.jpg';
import buildImg from '../images/products/kitchen.jpg';

/**
 * Magazine (Figma `1118:32065` header + `997:34870` cards, white section).
 *
 * Uses the shared `DiscoverHeader` ("(05 – Digital Magazines)", Popular
 * categories + Sign up CTA) above four tall coloured cards for the editorial
 * series (The Project Portfolio / Curated Collection / The Commercial Edit /
 * Build). The subscribe form now lives in the footer "(06 – Subscribe)".
 * Static promotional content; series imagery reuses homepage assets.
 */
/** The newsletter series, linked to the listing filtered by type (`?sort=`). */
const NEWSLETTER_CATEGORIES = [
    { label: 'Curated Collection', to: '/digital-magazines?sort=curated' },
    {
        label: 'The Commercial Edit',
        to: '/digital-magazines?sort=commercialedit',
    },
    {
        label: 'The Project Portfolio',
        to: '/digital-magazines?sort=projectportfolio',
    },
    { label: 'Build', to: '/digital-magazines?sort=build' },
];

interface MagazineCard {
    number: string;
    title: string;
    /** Magazine series this card links to (latest issue resolved server-side). */
    type: NewsletterTypeEnum;
    bg: string;
    titleColor: string;
    numberColor: string;
    /** Single image (cards 1, 3, 4). */
    image?: string;
    /** Three-image collage (card 2): two stacked left + one tall right. */
    collage?: { topLeft: string; bottomLeft: string; right: string };
    hasVideo?: boolean;
    /** Wider title column so a long title stays two lines (e.g. card 3). */
    wideTitle?: boolean;
}

const CARDS: MagazineCard[] = [
    {
        number: '01',
        title: 'The Project Portfolio',
        type: NewsletterTypeEnum.ProjectPortfolio,
        image: portfolioImg,
        bg: '#989485',
        titleColor: '#e5e5e5',
        numberColor: '#e5e5e5',
        hasVideo: true,
    },
    {
        number: '02',
        title: 'Curated Collection',
        type: NewsletterTypeEnum.CuratedCollection,
        collage: {
            topLeft: chairImg,
            bottomLeft: herringboneImg,
            right: stoveImg,
        },
        bg: '#000000',
        titleColor: '#b5b5b5',
        numberColor: '#d4d1cc',
    },
    {
        number: '03',
        title: 'The Commercial Edit',
        type: NewsletterTypeEnum.CommercialEdit,
        image: commercialImg,
        bg: '#d4d1cc',
        titleColor: '#343434',
        numberColor: '#1f1f21',
        wideTitle: true,
    },
    {
        number: '04',
        title: 'Build',
        type: NewsletterTypeEnum.Build,
        image: buildImg,
        bg: '#8e8580',
        titleColor: '#e5e5e5',
        numberColor: '#e5e5e5',
    },
];

export const MagazineSection = () => {
    const { homeCopy, magazineLinksByType } =
        useLoaderData<typeof homeLoader>();

    // US has no magazines yet — show a Coming Soon pill above the subscribe
    // field and make the preview cards non-interactive (copy stays as a teaser).
    const isComingSoon = useCountryContext().byCountry({
        US: true,
        AU: false,
        NZ: false,
    });

    return (
        <section className="w-full overflow-hidden bg-white text-[#1f1f21]">
            <DiscoverHeader
                theme="light"
                fullWidthRule
                eyebrow="(05 – Digital Magazines)"
                heading="Magazine quality content"
                categoriesLabel="Digital Newsletters"
                categories={NEWSLETTER_CATEGORIES}
                description={homeCopy.magazineDescription}
                actionSlot={
                    isComingSoon ? (
                        <>
                            <ComingSoonPill />
                            <SubscribeField className="md:w-[32.08vw]" />
                        </>
                    ) : (
                        <SubscribeField className="md:w-[32.08vw]" />
                    )
                }
            />

            {/* Editorial series cards — full-bleed, centre-aligned so partial
                cards peek at both edges and the loop reads as endless */}
            <ScrollReveal delay={120} className="max-md:py-6 md:py-[8.33vw]">
                <Carousel
                    options={{
                        align: 'center',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        // Start on card 02 so card 01 peeks on the left —
                        // starting at 0 makes the loop's left peek (card 04)
                        // read like the carousel begins at the end.
                        startIndex: 1,
                        // Wrap around at either end. Embla falls back to
                        // non-loop automatically when slides don't fill the view.
                        loop: true,
                    }}
                >
                    {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                        breaks at the loop seam; centre align needs symmetry). */}
                    <CarouselContent>
                        {/* Rendered twice: four cards (~1.2 viewports) aren't
                            enough content for embla to loop, so it silently
                            disables looping without the duplicate set. */}
                        {[...CARDS, ...CARDS].map((card, index) => (
                            <CarouselItem
                                key={`${card.number}-${index}`}
                                className="max-md:basis-[calc(38%+2.84vw)] max-md:px-[1.42vw] md:basis-[29.68vw] md:px-[1.04vw]"
                            >
                                <RoccoLink
                                    to={
                                        magazineLinksByType[card.type]?.link ??
                                        '/digital-magazines'
                                    }
                                    className={cn(
                                        'relative flex w-full flex-col overflow-hidden',
                                        isComingSoon && 'pointer-events-none',
                                    )}
                                    style={{
                                        backgroundColor: card.bg,
                                        aspectRatio: '530 / 906',
                                    }}
                                >
                                    <span
                                        className="absolute font-sans leading-none max-md:left-[2.3%] max-md:top-[1.6%] max-md:text-[3.16vw] md:left-[0.625vw] md:top-[0.77vw] md:text-[2.31vw]"
                                        style={{
                                            color: card.numberColor,
                                            letterSpacing: '-0.046vw',
                                        }}
                                    >
                                        {card.number}
                                    </span>
                                    <span
                                        className={cn(
                                            'absolute font-sans leading-none -tracking-[0.02em] max-md:left-[2.3%] max-md:top-[16.3%] max-md:text-[4.1vw] md:left-[0.625vw] md:top-[7.7vw] md:text-[3.125vw]',
                                            card.wideTitle
                                                ? 'md:w-[24.4vw]'
                                                : 'md:w-[20vw]',
                                        )}
                                        style={{ color: card.titleColor }}
                                    >
                                        {card.title}
                                    </span>
                                    <div className="absolute overflow-hidden max-md:inset-x-[2.3%] max-md:bottom-[2%] max-md:top-[32%] md:inset-x-[0.833vw] md:bottom-[1vw] md:top-[15.1vw]">
                                        {card.collage ? (
                                            <div className="grid size-full grid-cols-2 gap-[0.26vw]">
                                                <div className="grid grid-rows-2 gap-[0.26vw]">
                                                    <div className="overflow-hidden">
                                                        <img
                                                            src={
                                                                card.collage
                                                                    .topLeft
                                                            }
                                                            alt=""
                                                            aria-hidden="true"
                                                            className="size-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <img
                                                            src={
                                                                card.collage
                                                                    .bottomLeft
                                                            }
                                                            alt=""
                                                            aria-hidden="true"
                                                            className="size-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="overflow-hidden">
                                                    <img
                                                        src={card.collage.right}
                                                        alt=""
                                                        aria-hidden="true"
                                                        className="size-full object-cover"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <img
                                                    src={card.image}
                                                    alt={card.title}
                                                    className="absolute inset-0 size-full max-w-none object-cover"
                                                    loading="lazy"
                                                />
                                                {card.hasVideo && (
                                                    <span className="absolute left-1/2 top-1/2 flex aspect-square w-[40%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/80">
                                                        <span className="ml-1 size-0 border-y-[0.6vw] border-l-[1vw] border-y-transparent border-l-white" />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </RoccoLink>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </ScrollReveal>
        </section>
    );
};
